import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Input,
  Select,
  message,
  Typography,
  Divider,
} from "antd";
import {
  LeftOutlined,
  DoubleLeftOutlined,
  RightOutlined,
  DoubleRightOutlined,
  PlusOutlined,
  DeleteOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";
import {
  createOffice,
  deleteOffice,
  getCompanies,
  getLatestOffice,
  getOfficeById,
  getNextOfficeId,
  getOffices,
  updateOffice,
  type CompanyResponse,
  type OfficeResponse,
} from "../../utils/API";
import "./offices.css";

const { Title, Text } = Typography;
const { TextArea } = Input;

type OfficeRecord = {
  id?: number;
  localId: number;
  companyName: string;
  officeName: string;
  country: string;
  state: string;
  zipCode: string;
  box: string;
  addressLine1: string;
  addressLine2: string;
  shippingAddress: string;
  phone1: string;
  phone2: string;
  fax1: string;
  fax2: string;
  tollFreePhone: string;
  email: string;
  comment: string;
  officeUrl: string;
  officeEmail2: string;
};

const emptyOffice = (localId: number): OfficeRecord => ({
  id: undefined,
  localId,
  companyName: "",
  officeName: "",
  country: "",
  state: "",
  zipCode: "",
  box: "",
  addressLine1: "",
  addressLine2: "",
  shippingAddress: "",
  phone1: "",
  phone2: "",
  fax1: "",
  fax2: "",
  tollFreePhone: "",
  email: "",
  comment: "",
  officeUrl: "",
  officeEmail2: "",
});

const mapOffice = (item: OfficeResponse & Record<string, any>): OfficeRecord => ({
  id: item.id,
  localId: item.id,
  companyName: item.companyName || item.company_name || "",
  officeName: item.officeName || item.office_name || "",
  country: item.country || "",
  state: item.state || "",
  zipCode: item.zipCode || item.zip_code || "",
  box: item.box || "",
  addressLine1: item.addressLine1 || item.address_line_1 || "",
  addressLine2: item.addressLine2 || item.address_line_2 || "",
  shippingAddress: item.shippingAddress || item.shipping_address || "",
  phone1: item.phone1 || item.phone || "",
  phone2: item.phone2 || item.phone_2_fax || "",
  fax1: item.fax1 || item.fax_alt_phone || "",
  fax2: item.fax2 || "",
  tollFreePhone: item.tollFreePhone || item.toll_free_phone || "",
  email: item.email || "",
  comment: item.comment || item.comments || "",
  officeUrl: item.officeUrl || item.office_url || "",
  officeEmail2: item.officeEmail2 || item.office_email_2 || "",
});

export default function Offices() {
  const navigate = useNavigate();
  const [offices, setOffices] = useState<OfficeRecord[]>([]);
  const [companies, setCompanies] = useState<CompanyResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [recordIdInput, setRecordIdInput] = useState("");
  const [minOfficeId, setMinOfficeId] = useState<number | null>(null);
  const [maxOfficeId, setMaxOfficeId] = useState<number | null>(null);

  const currentOffice = offices[currentIndex];

  const companyOptions = useMemo(() => {
    const seen = new Set<string>();
    return companies
      .map((item) => String(item?.name || "").trim())
      .filter((name) => {
        if (!name) return false;
        const key = name.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((name) => ({ value: name, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [companies]);

  const fetchCompanies = async () => {
    try {
      const limit = 500;
      const first = await getCompanies({ page: 1, limit });
      const all = [...(first.data ?? [])];

      // Keep paging until we have them all — a fixed limit silently drops
      // companies off the end of the dropdown once the table outgrows it.
      const totalPages = first.pagination?.totalPages ?? 1;
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await getCompanies({ page, limit });
        all.push(...(next.data ?? []));
      }

      setCompanies(all);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load companies");
    }
  };

  const refreshOffices = async () => {
    try {
      setLoading(true);
      const response = await getOffices({
        page: 1,
        limit: 100000,
      });
      const records = response.data.map(mapOffice);
      setOffices(records);
      setTotalCount(response.pagination?.total ?? records.length);
      if (records.length > 0) {
        setMinOfficeId(records[0].id ?? records[0].localId);
        setMaxOfficeId(
          records[records.length - 1].id ?? records[records.length - 1].localId
        );
        setCurrentIndex(0);
      } else {
        setMinOfficeId(null);
        setMaxOfficeId(null);
        setCurrentIndex(0);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load offices";
      if (
        typeof errorMessage === "string" &&
        errorMessage.includes('column "state" does not exist')
      ) {
        return;
      }
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadLatestOffice = async () => {
    try {
      setLoading(true);
      const [latest, all] = await Promise.all([
        getLatestOffice(),
        getOffices({ page: 1, limit: 100000 }),
      ]);
      const records = all.data.map(mapOffice);
      setOffices(records);
      setTotalCount(all.pagination?.total ?? records.length);
      if (records.length > 0) {
        setMinOfficeId(records[0].id ?? records[0].localId);
        setMaxOfficeId(
          records[records.length - 1].id ?? records[records.length - 1].localId
        );
      } else {
        setMinOfficeId(null);
        setMaxOfficeId(null);
      }
      const latestRecord = mapOffice(latest as any);
      const latestIndex = records.findIndex(
        (item) => (item.id ?? item.localId) === (latestRecord.id ?? latestRecord.localId)
      );
      setCurrentIndex(latestIndex >= 0 ? latestIndex : Math.max(records.length - 1, 0));
      setRecordIdInput(
        String(latestRecord.id ?? latestRecord.localId ?? "")
      );
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to load offices";
      if (
        typeof errorMessage === "string" &&
        errorMessage.includes('column "state" does not exist')
      ) {
        return;
      }
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchOfficeById = async (id: number) => {
    try {
      setLoading(true);
      const response = await getOfficeById(id);
      const record = mapOffice(response);
      setOffices([record]);
      setCurrentIndex(0);
      setRecordIdInput(String(record.id ?? record.localId ?? id));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Office ID not found";
      if (
        typeof errorMessage === "string" &&
        errorMessage.includes('column "state" does not exist')
      ) {
        return;
      }
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    refreshOffices();
  }, []);

  useEffect(() => {
    setRecordIdInput(offices.length > 0 ? String(currentIndex + 1) : "");
  }, [currentIndex, offices.length]);

  const getNextLocalId = async () => {
    try {
      const response = await getNextOfficeId();
      return Number(response.nextId || 1);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to get next id");
      const fallbackMax = offices.reduce(
        (max, item) => Math.max(max, Number(item.localId || 0)),
        0
      );
      return fallbackMax + 1;
    }
  };

  const updateLocal = (patch: Partial<OfficeRecord>) => {
    if (!currentOffice) return;
    setOffices((prev) =>
      prev.map((item, idx) =>
        idx === currentIndex ? { ...item, ...patch } : item
      )
    );
  };

  const persistOffice = async (record: OfficeRecord) => {
    const officeName = record.officeName.trim();
    if (!officeName) {
      message.warning("Office Name is required");
      return;
    }

    const payload: any = {
      id: record.id ?? record.localId,
      office_name: officeName,
    };
    if (record.companyName.trim())
      payload.company_name = record.companyName.trim();
    if (record.country.trim()) payload.country = record.country.trim();
    if (record.state.trim()) payload.state = record.state.trim();
    if (record.zipCode.trim()) payload.zip_code = record.zipCode.trim();
    if (record.box.trim()) payload.box = record.box.trim();
    if (record.addressLine1.trim())
      payload.address_line_1 = record.addressLine1.trim();
    if (record.addressLine2.trim())
      payload.address_line_2 = record.addressLine2.trim();
    if (record.shippingAddress.trim())
      payload.shipping_address = record.shippingAddress.trim();
    if (record.phone1.trim()) payload.phone = record.phone1.trim();
    if (record.phone2.trim()) payload.phone_2_fax = record.phone2.trim();
    if (record.fax1.trim()) payload.fax_alt_phone = record.fax1.trim();
    if (record.fax2.trim()) payload.fax2 = record.fax2.trim();
    if (record.tollFreePhone.trim())
      payload.toll_free_phone = record.tollFreePhone.trim();
    if (record.email.trim()) payload.email = record.email.trim();
    if (record.comment.trim()) payload.comments = record.comment.trim();
    if (record.officeUrl.trim()) payload.office_url = record.officeUrl.trim();
    if (record.officeEmail2.trim())
      payload.office_email_2 = record.officeEmail2.trim();

    try {
      if (!record.id) {
        const created = await createOffice(payload);
        setOffices((prev) =>
          prev.map((item, idx) =>
            idx === currentIndex ? { ...item, id: created.id } : item
          )
        );
        setMinOfficeId((prev) =>
          prev === null ? created.id : Math.min(prev, created.id)
        );
        setMaxOfficeId((prev) =>
          prev === null ? created.id : Math.max(prev, created.id)
        );
      } else {
        await updateOffice(record.id, payload);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to save office";
      if (
        typeof errorMessage === "string" &&
        errorMessage.includes('column "state" does not exist')
      ) {
        return;
      }
      message.error(errorMessage);
    }
  };

  const handleAdd = async () => {
    const nextId = await getNextLocalId();
    const newOffice = emptyOffice(nextId);
    setOffices((prev) => [newOffice, ...prev]);
    setCurrentIndex(0);
  };

  const handleDelete = async () => {
    if (!currentOffice) return;
    const confirmDelete = window.confirm("Delete this office record?");
    if (!confirmDelete) return;

    try {
      if (!currentOffice.id) {
        setOffices((prev) => prev.filter((_, idx) => idx !== currentIndex));
        setCurrentIndex(0);
        return;
      }
      await deleteOffice(currentOffice.id);
      // message.success("Office deleted successfully");
      await refreshOffices();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete office");
    }
  };

  const handleRecordJump = async (value: string) => {
    const target = Number(value);
    if (!Number.isFinite(target) || target <= 0) {
      return;
    }
    if (target > offices.length) {
      setRecordIdInput(offices.length > 0 ? String(currentIndex + 1) : "");
      return;
    }
    setCurrentIndex(target - 1);
  };

  const handlePrev = async () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleNext = async () => {
    if (currentIndex < offices.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  return (
    <div className="offices-page">
      <div className="flex justify-end mb-2">
        <PageLogoutButton />
      </div>
      <div className="offices-toprow">
        <div className="offices-header">
          <Button
            icon={<LeftOutlined />}
            size="small"
            className="offices-back"
            onClick={() => navigate("/ate-dashboard")}
          />
          <NavigationDropdown />
          <Title level={4} className="offices-title">
            Offices Manager
          </Title>
        </div>

        <div className="offices-recordbar">
          <div className="offices-recordbar-left">
            <Button
              size="small"
              icon={<DoubleLeftOutlined />}
              onClick={() =>
                offices.length > 0 &&
                setCurrentIndex((prev) => Math.max(0, prev - 100))
              }
            />
            <Button size="small" icon={<LeftOutlined />} onClick={handlePrev} />
            <div className="flex flex-col items-center">
              <Input
                size="small"
                className="offices-record-input"
                value={recordIdInput}
                onChange={(e) => setRecordIdInput(e.target.value)}
                onPressEnter={(e) => handleRecordJump(e.currentTarget.value)}
                onBlur={(e) => handleRecordJump(e.currentTarget.value)}
              />
              <div style={{ width: "100%", height: 2, background: "#3b82f6", marginTop: 2 }}></div>
              <span style={{ fontSize: 9, color: "#9ca3af", fontWeight: 500 }}>Records</span>
            </div>
            <Button size="small" icon={<RightOutlined />} onClick={handleNext} />
            <Button
              size="small"
              icon={<DoubleRightOutlined />}
              onClick={() =>
                offices.length > 0 &&
                setCurrentIndex((prev) =>
                  Math.min(offices.length - 1, prev + 100),
                )
              }
            />
            <div className="flex flex-col leading-tight" style={{ marginLeft: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>
                {currentIndex + 1} / {totalCount}
              </span>
              <span style={{ fontSize: 9, color: "#9ca3af" }}>Open / Total</span>
            </div>
          </div>
          <div className="offices-recordbar-actions">
            <Button size="small" onClick={refreshOffices}>
              Show All
            </Button>
            <Button size="small" icon={<PlusOutlined />} onClick={handleAdd}>
              New Record
            </Button>
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={handleDelete}
            >
              Delete Record
            </Button>
            <Button size="small" icon={<SortAscendingOutlined />}>
              Sort
            </Button>
          </div>
        </div>
      </div>

      <div className="offices-card">
        <div className="offices-row">
          <div className="offices-label">Company Name:</div>
          <Select
            className="offices-input offices-wide"
            options={companyOptions}
            value={currentOffice?.companyName || undefined}
            placeholder="Company Name"
            showSearch
            filterOption={(input, option) => String(option?.label ?? "").toLowerCase().startsWith(input.toLowerCase())}
            onChange={(value) => updateLocal({ companyName: value })}
            onBlur={() => currentOffice && persistOffice(currentOffice)}
          />
        </div>

        <div className="offices-row">
          <div className="offices-label">Office Name:</div>
          <Input
            className="offices-input offices-wide"
            value={currentOffice?.officeName || ""}
            onChange={(e) => updateLocal({ officeName: e.target.value })}
            onBlur={() => currentOffice && persistOffice(currentOffice)}
          />
        </div>

        <div className="offices-section-title">Address:</div>

        <div className="offices-grid-2">
          <div className="offices-row">
            <div className="offices-label-small">Country:</div>
            <Input
              className="offices-input"
              value={currentOffice?.country || ""}
              onChange={(e) => updateLocal({ country: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">State:</div>
            <Input
              className="offices-input"
              value={currentOffice?.state || ""}
              onChange={(e) => updateLocal({ state: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">Zip Code:</div>
            <Input
              className="offices-input"
              value={currentOffice?.zipCode || ""}
              onChange={(e) => updateLocal({ zipCode: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">Box#:</div>
            <Input
              className="offices-input"
              value={currentOffice?.box || ""}
              onChange={(e) => updateLocal({ box: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
        </div>

        <div className="offices-stack">
          <div className="offices-row">
            <div className="offices-label">Address Line 1:</div>
            <TextArea
              rows={2}
              className="offices-textarea"
              value={currentOffice?.addressLine1 || ""}
              onChange={(e) => updateLocal({ addressLine1: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label">Address Line 2:</div>
            <TextArea
              rows={2}
              className="offices-textarea"
              value={currentOffice?.addressLine2 || ""}
              onChange={(e) => updateLocal({ addressLine2: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label">Shipping Address:</div>
            <TextArea
              rows={2}
              className="offices-textarea"
              value={currentOffice?.shippingAddress || ""}
              onChange={(e) => updateLocal({ shippingAddress: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
        </div>

        <div className="offices-section-title">Contact Details:</div>

        <div className="offices-grid-2">
          <div className="offices-row">
            <div className="offices-label-small">Phone:</div>
            <Input
              className="offices-input"
              value={currentOffice?.phone1 || ""}
              onChange={(e) => updateLocal({ phone1: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">Phone2:</div>
            <Input
              className="offices-input"
              value={currentOffice?.phone2 || ""}
              onChange={(e) => updateLocal({ phone2: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">Fax:</div>
            <Input
              className="offices-input"
              value={currentOffice?.fax1 || ""}
              onChange={(e) => updateLocal({ fax1: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">Fax2:</div>
            <Input
              className="offices-input"
              value={currentOffice?.fax2 || ""}
              onChange={(e) => updateLocal({ fax2: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">ToolFree Phone:</div>
            <Input
              className="offices-input"
              value={currentOffice?.tollFreePhone || ""}
              onChange={(e) => updateLocal({ tollFreePhone: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">eMail:</div>
            <Input
              className="offices-input"
              value={currentOffice?.email || ""}
              onChange={(e) => updateLocal({ email: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
        </div>

        <div className="offices-row">
          <div className="offices-label">Comments:</div>
          <TextArea
            rows={2}
            className="offices-textarea"
            value={currentOffice?.comment || ""}
            onChange={(e) => updateLocal({ comment: e.target.value })}
            onBlur={() => currentOffice && persistOffice(currentOffice)}
          />
        </div>

        <div className="offices-grid-2">
          <div className="offices-row">
            <div className="offices-label-small">OfficeURL:</div>
            <Input
              className="offices-input"
              value={currentOffice?.officeUrl || ""}
              onChange={(e) => updateLocal({ officeUrl: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
          <div className="offices-row">
            <div className="offices-label-small">OfficeEmail2:</div>
            <Input
              className="offices-input"
              value={currentOffice?.officeEmail2 || ""}
              onChange={(e) => updateLocal({ officeEmail2: e.target.value })}
              onBlur={() => currentOffice && persistOffice(currentOffice)}
            />
          </div>
        </div>

        {loading && <Text className="offices-loading">Loading...</Text>}
      </div>
    </div>
  );
}
